/**
 * common/http.js — the single network seam.
 *
 * Every API call in api.js / api_external.js goes through here. This is the ONLY
 * place that knows about fetch, headers, auth tokens, timeouts, and JSON parsing.
 *
 * Mock switch: when CONFIG.USE_MOCK is true, requests are routed to js/mock.js
 * instead of the network (dynamically imported, so a real build can drop the mock
 * + JSON entirely). api.js is identical in both modes.
 */
import CONFIG from "../config/config.js";
import { anonymous, session } from "./storage.js";
import { buildUrl } from "./utils.js";
import { ApiError, normalizeError, codeForStatus } from "./errors.js";

async function request(method, path, { query, body, headers, signal } = {}) {
  // ── Mock mode ──────────────────────────────────────────────
  if (CONFIG.USE_MOCK) {
    const { mockRouter } = await import("../api/mock.js");
    return mockRouter({ method, path, query, body, token: session.getToken() });
  }

  // ── Real network ───────────────────────────────────────────
  const url = buildUrl(CONFIG.API_BASE_URL, path, query);
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), CONFIG.API_TIMEOUT_MS);
  const token = session.getToken();

  const h = { Accept: "application/json", ...(headers || {}) };
  if (body !== undefined) h["Content-Type"] = "application/json";
  if (token) h[CONFIG.TOKEN_HEADER] = `${CONFIG.TOKEN_SCHEME} ${token}`;
  if (path.includes("/images/") && (path.endsWith("/like") || path.endsWith("/save"))) {
    h["X-Prompto-Anonymous-ID"] = anonymous.getID();
  }

  try {
    const res = await fetch(url, {
      method,
      headers: h,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: signal || ctrl.signal,
      credentials: "include",
    });
    clearTimeout(timer);

    const text = await res.text();
    const data = text ? safeJson(text) : null;

    if (!res.ok) {
      const msg = (data && (data.message || data.error)) || `HTTP ${res.status}`;
      throw new ApiError(msg, { status: res.status, code: codeForStatus(res.status), details: data });
    }
    if (CONFIG.DEBUG) console.debug("[http]", method, url, "→", res.status);
    recordDiagnostic({ method, path, ok: true, status: res.status });
    return data;
  } catch (e) {
    clearTimeout(timer);
    const error = normalizeError(e);
    recordDiagnostic({ method, path, ok: false, status: error.status, code: error.code, message: error.message });
    throw error;
  }
}

function safeJson(text) { try { return JSON.parse(text); } catch (e) { return text; } }

function recordDiagnostic(entry) {
  try {
    if (typeof window !== "undefined") window.PromptoRuntime?.recordRequest?.(entry);
  } catch (e) {}
}

export const http = {
  get: (path, opts) => request("GET", path, opts),
  post: (path, body, opts) => request("POST", path, { ...opts, body }),
  put: (path, body, opts) => request("PUT", path, { ...opts, body }),
  patch: (path, body, opts) => request("PATCH", path, { ...opts, body }),
  del: (path, opts) => request("DELETE", path, opts),
  request,
};

export default http;
